'use client';

import { Button, Spinner, Typography, ChevronDownIcon, ChevronUpIcon } from '@ag1/ui';
import { usePageContext } from '@portal/processes/Page/PageContext';
import { LanguageValue, localeEnumToLanguageName, LocaleEnum } from '@portal/schema/locale';
import { StoreEnum } from '@portal/schema/storeCode';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { Popout } from '@portal/shared/ui/Popout';
import { IngredientLabelGroqResult } from '@portal/shared/utils/groq/ingredientLabelGroq';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState, useMemo, useEffect } from 'react';
import IconClose from 'shared/ui/Icon/24/IconClose';
import { fetcher } from 'shared/utils/fetcher';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import type { IngredientProductSelectorConfig, IngredientLabel } from 'types/studio/studio';
import FlavorSelection from '@portal/shared/components/FlavorSelection/FlavorSelection';
import { AlternateFlavor, AlternateFlavorConfig } from '@portal/processes/ShopV2/ShopV2.types';
import styles from './IngredientModal.module.scss';
import { IngredientModalDE } from './IngredientModalDE/IngredientModalDE';
import { IngredientModalEN } from './IngredientModalEN/IngredientModalEN';
import { ProductSelector } from './components/ProductSelector/ProductSelector';

const HIDDEN_LANGUAGE_STORES: StoreEnum[] = [
  StoreEnum.dev,
  StoreEnum.qa,
  StoreEnum.us,
  StoreEnum.ukd,
  StoreEnum.uk
];

type IngredientModalProps = {
  isOpen: boolean;
  onClose: () => void;
  theme?: 'old' | 'new';
  isV53?: boolean;
  ingredientProducts?: IngredientProductSelectorConfig[] | null;
  supplementType?: 'single' | 'multi';
  showFlavors?: boolean;
};

const IngredientModal = ({
  isOpen,
  onClose,
  theme,
  isV53,
  ingredientProducts,
  supplementType = 'single',
  showFlavors = true
}: IngredientModalProps) => {
  const { page } = usePageContext();
  const locale = useLocale();
  const store = useStoreValue();
  const shouldUseV53 = useMemo(
    () => locale === LocaleEnum.enUS || (isV53 ?? page.v53Page),
    [locale, isV53, page.v53Page]
  );

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageValue>(
    () => localeEnumToLanguageName(locale) ?? 'english'
  );

  const [selectedProductHandle, setSelectedProductHandle] = useState<string | null>(null);
  const [selectedFlavorName, setSelectedFlavorName] = useState<string | null>(null);

  const showProductSelector = useMemo(
    () => !!ingredientProducts && ingredientProducts.length > 0,
    [ingredientProducts]
  );

  const shouldShowProductSelector = supplementType === 'multi' && showProductSelector;

  const { data: ingredientData = { v53: [], legacy: [] }, isPending: isLoadingIngredientData } =
    useQuery({
      queryKey: ['ingredientLabels', locale],
      queryFn: async () => {
        const result = await fetcher.get<{ ingredientLabels: IngredientLabelGroqResult }>(
          `/api/products/get-ingredient-labels/${locale}`
        );
        return result.status === 200 ? result.data.ingredientLabels : { v53: [], legacy: [] };
      },
      enabled: isOpen
    });

  const selectedProductConfig = useMemo(() => {
    if (!ingredientProducts || !selectedProductHandle) return null;
    return ingredientProducts.find((p) => p.productHandle === selectedProductHandle) || null;
  }, [ingredientProducts, selectedProductHandle]);

  const availableFlavorsForSelectedProduct = useMemo(() => {
    return selectedProductConfig?.availableFlavors || [];
  }, [selectedProductConfig]);

  // Map Sanity flavor config to the structure expected by FlavorSelection
  const mappedFlavorVariants: AlternateFlavorConfig[] = useMemo(() => {
    return (availableFlavorsForSelectedProduct || [])
      .filter(
        (
          flavor
        ): flavor is typeof flavor & {
          title: string;
          color: string;
          _key: string;
        } =>
          typeof flavor.title === 'string' &&
          typeof flavor.color === 'string' &&
          typeof flavor._key === 'string'
      )
      .map((flavor) => ({
        title: flavor.title,
        color: flavor.color,
        hoverColor: flavor.hoverColor,
        id: flavor._key,
        fontColor: flavor.title === 'Original' ? 'white' : undefined
      }));
  }, [availableFlavorsForSelectedProduct]);

  useEffect(() => {
    // Handle case where ingredientProducts is not provided
    if (!ingredientProducts || ingredientProducts.length === 0) {
      const defaultHandle = shouldUseV53 ? 'V53Pouch' : 'V52Pouch';
      setSelectedProductHandle(defaultHandle);
      setSelectedFlavorName(null);
    } else {
      const currentSelectionIsValid = ingredientProducts.some(
        (p) => p.productHandle === selectedProductHandle
      );

      let targetProductConfig: IngredientProductSelectorConfig | undefined;
      let targetHandle: string | null = null;

      if (!selectedProductHandle || !currentSelectionIsValid) {
        const [firstProduct] = ingredientProducts;
        targetProductConfig = firstProduct;
        targetHandle = targetProductConfig?.productHandle ?? null;

        if (targetHandle) {
          setSelectedProductHandle(targetHandle);
        } else {
          setSelectedProductHandle(null);
          setSelectedFlavorName(null);
          return; // Exit early if no valid product handle found
        }
      } else {
        targetProductConfig = ingredientProducts.find(
          (p) => p.productHandle === selectedProductHandle
        );

        targetHandle = selectedProductHandle;
      }

      if (targetProductConfig) {
        const flavors = targetProductConfig.availableFlavors || [];
        const initialFlavor = targetProductConfig.initialFlavor || null;
        const isValidInitialFlavor = flavors.some((f) => f.title === initialFlavor);

        if (isValidInitialFlavor) {
          setSelectedFlavorName(initialFlavor);
        } else if (flavors.length > 0) {
          setSelectedFlavorName(flavors[0]?.title || null);
        } else {
          setSelectedFlavorName(null);
        }
      } else {
        setSelectedFlavorName(null);
      }
    }
  }, [ingredientProducts, selectedProductHandle, shouldUseV53]);

  const handleFlavorChange = (flavor: AlternateFlavor | AlternateFlavorConfig) => {
    setSelectedFlavorName(flavor.title || null);
  };

  const handleProductSelect = (newHandle: string) => {
    setSelectedProductHandle(newHandle);
    const newProductConfig = ingredientProducts?.find((p) => p.productHandle === newHandle);
    const initialFlavor = newProductConfig?.initialFlavor || null;
    const isValidInitial = newProductConfig?.availableFlavors?.some(
      (f) => f.title === initialFlavor
    );
    setSelectedFlavorName(
      isValidInitial ? initialFlavor : newProductConfig?.availableFlavors?.[0]?.title || null
    );
  };

  const availableLabels = useMemo(() => {
    if (!ingredientData) return [];
    const baseLabels = shouldUseV53
      ? [...ingredientData.v53, ...ingredientData.legacy]
      : ingredientData.legacy;

    let productFilteredLabels: IngredientLabel[] = [];
    if (selectedProductHandle) {
      productFilteredLabels = baseLabels.filter(
        (label) =>
          typeof label.ingredientLabelDetails?.product === 'string' &&
          label.ingredientLabelDetails.product.includes(selectedProductHandle)
      );
    } else {
      productFilteredLabels = [];
      if (supplementType === 'single' && ingredientProducts && ingredientProducts.length > 0) {
        const firstHandle = ingredientProducts[0].productHandle;
        if (firstHandle) {
          productFilteredLabels = baseLabels.filter(
            (label) =>
              typeof label.ingredientLabelDetails?.product === 'string' &&
              label.ingredientLabelDetails.product.includes(firstHandle)
          );
        }
      }
    }

    if (selectedFlavorName && availableFlavorsForSelectedProduct.length > 0) {
      return productFilteredLabels.filter((label) => {
        const labelFlavor = label.ingredientLabelDetails?.flavor;
        return labelFlavor === selectedFlavorName || labelFlavor == null;
      });
    }

    return productFilteredLabels;
  }, [
    ingredientData,
    selectedProductHandle,
    supplementType,
    ingredientProducts,
    selectedFlavorName,
    availableFlavorsForSelectedProduct,
    shouldUseV53
  ]);

  const activeFlavor = useMemo(() => {
    if (!selectedFlavorName) return null;
    const foundFlavor = mappedFlavorVariants.find((f) => f.title === selectedFlavorName);
    return foundFlavor || null;
  }, [mappedFlavorVariants, selectedFlavorName]);

  const activeFlavorId = activeFlavor?.id || '';

  const singleProductTitle = useMemo(() => {
    if (supplementType === 'single' || supplementType === undefined) {
      if (ingredientProducts && ingredientProducts.length > 0) {
        return ingredientProducts[0]?.title ?? null;
      }
      // Apply default title logic here
      return shouldUseV53 ? 'AG1 Next Gen' : 'AG1 Pouch';
    }
    return null;
  }, [supplementType, ingredientProducts, shouldUseV53]);

  if (!isOpen) return null;

  // Generate options using title-cased language value
  const uniqueLanguages = new Set(
    availableLabels.map((label) => label.languagevalue).filter(Boolean)
  );
  const languageOptions = Array.from(uniqueLanguages).map((language) => {
    return {
      value: language,
      label: language ? language.charAt(0).toUpperCase() + language.slice(1) : 'Language'
    };
  });

  const selectedLabel = availableLabels.find((label) => label.languagevalue === selectedLanguage);
  // console.log('selectedLabel', selectedLabel);

  const canShowLanguageSelector =
    !isLoadingIngredientData &&
    !HIDDEN_LANGUAGE_STORES.includes(store) &&
    languageOptions.length > 1;

  return (
    <div className={clsx(styles.root, getThemeCSSClass(theme))}>
      <div className={styles.modalInner}>
        {(isLoadingIngredientData || (shouldShowProductSelector && !selectedProductHandle)) && (
          <Spinner color="brand-green" size={64} withOverlay={false} className={styles.spinner} />
        )}

        {!isLoadingIngredientData && (
          <div
            className={clsx(
              styles.modalHeader,
              canShowLanguageSelector && styles.modalHeaderWithLanguageSelector
            )}>
            <Typography
              className={styles.modalTitle}
              variant="headlineSans"
              size="3"
              component="h2">
              {selectedLabel?.ingredientLabelDetails?.title}
            </Typography>
            <div className={styles.modalControls}>
              {canShowLanguageSelector && (
                <Popout
                  options={languageOptions}
                  value={{
                    value: selectedLanguage,
                    label: selectedLanguage
                      ? selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)
                      : 'Language'
                  }}
                  onChange={(newValue) => {
                    if (newValue) {
                      setSelectedLanguage(newValue?.value as LanguageValue);
                    }
                  }}
                  renderAddon={({ getTargetButtonProps, isOpen: isLanguageSelectorOpen }) => (
                    <Button
                      {...getTargetButtonProps()}
                      variant="outline"
                      className={styles.selectWrapper}
                      icon={
                        isLanguageSelectorOpen ? (
                          <ChevronUpIcon className={styles.selectMenuArrow} />
                        ) : (
                          <ChevronDownIcon className={styles.selectMenuArrow} />
                        )
                      }
                      iconPosition="right">
                      {selectedLanguage
                        ? selectedLanguage.charAt(0).toUpperCase() + selectedLanguage.slice(1)
                        : 'Language'}
                    </Button>
                  )}
                />
              )}
              <button
                type="button"
                className={styles.modalButtonClose}
                onClick={onClose}
                aria-label="Close ingredients modal">
                <IconClose className={styles.iconClose} />
              </button>
            </div>
          </div>
        )}

        {!isLoadingIngredientData && shouldShowProductSelector && (
          <hr className={clsx(styles.separator, styles.separatorMarginBottom)} />
        )}

        {!isLoadingIngredientData && shouldShowProductSelector && ingredientProducts && (
          <ProductSelector
            products={ingredientProducts}
            selectedProductHandle={selectedProductHandle}
            onSelectProduct={handleProductSelect}
          />
        )}

        {!isLoadingIngredientData && (
          <hr
            className={clsx(
              styles.separator,
              !shouldShowProductSelector && styles.separatorNoMargin,
              shouldShowProductSelector && styles.separatorMarginBottom
            )}
          />
        )}

        {!isLoadingIngredientData && supplementType === 'single' && singleProductTitle && (
          <Typography
            variant="headline"
            size="1"
            component="h1"
            color="black-new"
            className={styles.singleProductTitle}>
            {singleProductTitle}
          </Typography>
        )}

        {!isLoadingIngredientData &&
          shouldUseV53 &&
          showFlavors &&
          availableFlavorsForSelectedProduct.length > 0 &&
          (() => {
            const validFlavors = mappedFlavorVariants.filter(
              (flavor): flavor is AlternateFlavorConfig =>
                typeof flavor.title === 'string' &&
                typeof flavor.color === 'string' &&
                typeof flavor.id === 'string'
            );

            if (validFlavors.length === 0) {
              return null;
            }

            return (
              <div className={styles.flavorSelectorWrapper}>
                <FlavorSelection
                  flavorVariants={validFlavors}
                  activeVariantId={activeFlavorId}
                  onChange={handleFlavorChange}
                />
              </div>
            );
          })()}

        {!isLoadingIngredientData && selectedLabel?.ingredientLabelDetails && (
          <div className={styles.ingredientModalContent}>
            {selectedLanguage === 'german' ? (
              <IngredientModalDE props={selectedLabel.ingredientLabelDetails} />
            ) : (
              <IngredientModalEN props={selectedLabel.ingredientLabelDetails} />
            )}
          </div>
        )}
        {!isLoadingIngredientData && !selectedLabel && (
          <div className={styles.noContentMessage}>
            <p>Ingredient information not available for the selected product and language.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IngredientModal;
